/**
 * Las Estadísticas de la marca en palabras (fase 5c, diseño `Estadisticas.dc.html`): el período, los cuatro números,
 * cada grupo y el recorrido por canal. Puro y testeado (`estadisticas.test.ts`).
 */

import { num, plural } from "../../campana-nueva/utils/format";
import type { Estadisticas, GrupoDeLasEstadisticas, Periodo } from "../types";
import { conSigno, decimal, pesos } from "./resultado";

export const R_ESTADISTICAS = "/activa/estadisticas";

export const PERIODOS: { id: Periodo; texto: string }[] = [
  { id: "30", texto: "Últimos 30 días" },
  { id: "90", texto: "Últimos 90 días" },
  { id: "todo", texto: "Todo" },
];

const CANAL = { email: "Email", whatsapp: "WhatsApp" } as const;

/** Vendiste de más, facturaron los que recibieron, descuentos usados y mensajes enviados. Sin firmeza, «≈ $0». */
export function tarjetasDeLasEstadisticas(e: Estadisticas): { titulo: string; valor: string; detalle: string }[] {
  const t = e.total;
  return [
    {
      titulo: "Vendiste de más",
      valor: t.firme && t.vendisteDeMas > 0 ? pesos(t.vendisteDeMas) : "≈ $0",
      detalle: `${conSigno(t.compradoresDeMas)} compradores · ${t.firme ? "firme" : "todavía no es firme"}`,
    },
    { titulo: "Facturaron los que recibieron", valor: pesos(t.facturacion), detalle: `${num(t.compraron)} ${plural(t.compraron, "comprador", "compradores")}` },
    { titulo: "Descuentos usados", valor: pesos(t.descuentoEstimado), detalle: `${num(t.usaronCupon)} ${plural(t.usaronCupon, "cupón", "cupones")} · estimado` },
    {
      titulo: "Mensajes enviados",
      valor: num(t.enviados),
      detalle: `${num(e.campanas)} ${plural(e.campanas, "campaña", "campañas")} · ${num(t.grupos)} ${plural(t.grupos, "grupo", "grupos")}`,
    },
  ];
}

export type TonoPildora = "verde" | "rojo" | "gris";

/** La fila de un grupo: por dónde salió y en cuántas campañas, los números y si ya se ve una diferencia. */
export function filaDeEstadisticas(g: GrupoDeLasEstadisticas): { como: string; facturacion: string; recibieron: string; control: string; deMas: string; pildora: { texto: string; tono: TonoPildora } } {
  const canales = g.canales.map((c) => CANAL[c]).join(" y ");
  const pildora: { texto: string; tono: TonoPildora } = !g.medible
    ? { texto: "Sin control", tono: "gris" }
    : g.firme
      ? g.compradoresDeMas > 0
        ? { texto: "Vende de más", tono: "verde" }
        : { texto: "Vende menos", tono: "rojo" }
      : { texto: "Sin diferencia, por ahora", tono: "gris" };
  return {
    como: `${canales} · ${num(g.campanas)} ${plural(g.campanas, "campaña", "campañas")}`,
    facturacion: pesos(g.facturacion),
    recibieron: decimal(g.recibieron.porMil),
    control: g.medible ? decimal(g.control.porMil) : "—",
    deMas: g.medible ? `${conSigno(g.compradoresDeMas)} compradores` : "—",
    pildora,
  };
}

/** El ancho de las dos barras de un grupo, sobre el más alto de todos los grupos (el más alto llega al 90 %). */
export function anchoSobre(maximo: number, x: number): string {
  if (!(maximo > 0)) return "0%";
  return `${Math.max(x > 0 ? 2 : 0, Math.round((x / maximo) * 90))}%`;
}
